/**
 * RealmSkyTintSystem (AGE-1150) — always-on per-realm sky gradient.
 *
 * Draws a camera-fixed, scroll-factor-0 gradient rectangle behind all
 * gameplay layers as an orientation anchor.  Each realm has a unique
 * horizon tint:
 *
 *   - Verdana Fields  → soft green haze
 *   - Sablewind Reaches → warm amber
 *   - Frostmere Expanse → pale blue
 *   - Ember Mire → deep red glow
 *
 * When the player crosses a realm boundary the gradient cross-fades over
 * ~700 ms via a tween proxy.  Outside any realm the gradient fades to
 * transparent.
 *
 * Depth: 95 (behind BiomeDetailSystem at 96.5, RestorationTierRenderer
 * at 97, and everything else).
 *
 * Spec: `docs/design/wayfinding-navigation.md` §5.1 "Realm sky tint".
 */
import * as Phaser from 'phaser';
import { RealmId } from '@game/shared';
import { isWebGL } from '../rendering/rendererUtils';

// ── Constants ────────────────────────────────────────────────────────────

const SKY_DEPTH = 95;
const CROSSFADE_MS = 700;

/** Per-realm sky tint: top color (zenith) and bottom color (horizon). */
interface SkyGradient {
  topColor: number;
  topAlpha: number;
  bottomColor: number;
  bottomAlpha: number;
}

const REALM_SKY_GRADIENTS: Record<RealmId, SkyGradient> = {
  [RealmId.VerdanaFields]: {
    topColor: 0x2a6e2a,
    topAlpha: 0.03,
    bottomColor: 0x6abf6a,
    bottomAlpha: 0.12,
  },
  [RealmId.SablewindReaches]: {
    topColor: 0x8b6914,
    topAlpha: 0.04,
    bottomColor: 0xd4a040,
    bottomAlpha: 0.14,
  },
  [RealmId.FrostmereExpanse]: {
    topColor: 0x4080b0,
    topAlpha: 0.04,
    bottomColor: 0xa0d0e8,
    bottomAlpha: 0.13,
  },
  [RealmId.EmberMire]: {
    topColor: 0x601010,
    topAlpha: 0.05,
    bottomColor: 0xc03020,
    bottomAlpha: 0.16,
  },
};

/** Transparent "no realm" state. */
const CLEAR_GRADIENT: SkyGradient = {
  topColor: 0x000000,
  topAlpha: 0,
  bottomColor: 0x000000,
  bottomAlpha: 0,
};

// ── Color helpers ────────────────────────────────────────────────────────

function channelLerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = channelLerp(ar, br, t);
  const g = channelLerp(ag, bg, t);
  const bl = channelLerp(ab, bb, t);
  return (r << 16) | (g << 8) | bl;
}

function lerpGradient(a: SkyGradient, b: SkyGradient, t: number): SkyGradient {
  return {
    topColor: lerpColor(a.topColor, b.topColor, t),
    topAlpha: a.topAlpha + (b.topAlpha - a.topAlpha) * t,
    bottomColor: lerpColor(a.bottomColor, b.bottomColor, t),
    bottomAlpha: a.bottomAlpha + (b.bottomAlpha - a.bottomAlpha) * t,
  };
}

// ── System ───────────────────────────────────────────────────────────────

export class RealmSkyTintSystem {
  private readonly scene: Phaser.Scene;

  /** The full-screen gradient texture drawn once per resize/transition. */
  private canvas: Phaser.GameObjects.Image | null = null;
  private textureKey = '__realm_sky_tint__';

  private currentGradient: SkyGradient = { ...CLEAR_GRADIENT };
  private targetGradient: SkyGradient = { ...CLEAR_GRADIENT };
  private tween: Phaser.Tweens.Tween | null = null;

  private readonly resizeListener: () => void;
  private destroyed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.resizeListener = () => this.rebuildTexture();
    this.scene.scale.on(Phaser.Scale.Events.RESIZE, this.resizeListener);
  }

  /** Called when the local player's realm changes. `null` = left all realms. */
  setRealm(realmId: RealmId | null): void {
    if (this.destroyed) return;
    if (!isWebGL(this.scene)) return;

    this.targetGradient = realmId
      ? { ...REALM_SKY_GRADIENTS[realmId] }
      : { ...CLEAR_GRADIENT };

    this.animateToTarget();
  }

  /**
   * AGE-1174: Directly set the sky gradient to an interpolated position between
   * two realms during the three-stage transition. Bypasses the tween animation —
   * the BiomeTransitionSystem drives this per-frame.
   *
   * @param fromRealm - Source realm (or null for "no realm")
   * @param toRealm   - Target realm (or null for "no realm")
   * @param progress  - 0 = fully in `fromRealm`, 1 = fully in `toRealm`
   */
  setTransitionProgress(
    fromRealm: RealmId | null,
    toRealm: RealmId | null,
    progress: number,
  ): void {
    if (this.destroyed) return;
    if (!isWebGL(this.scene)) return;

    // Kill any active tween so it doesn't fight with per-frame updates.
    if (this.tween) {
      this.tween.stop();
      this.tween = null;
    }

    const from = fromRealm ? REALM_SKY_GRADIENTS[fromRealm] : CLEAR_GRADIENT;
    const to = toRealm ? REALM_SKY_GRADIENTS[toRealm] : CLEAR_GRADIENT;
    const t = Phaser.Math.Clamp(progress, 0, 1);

    this.currentGradient = lerpGradient(from, to, t);
    this.targetGradient = { ...this.currentGradient };
    this.rebuildTexture();
  }

  // ── Private ────────────────────────────────────────────────────────────

  private animateToTarget(): void {
    if (this.tween) {
      this.tween.stop();
      this.tween = null;
    }

    const from = { ...this.currentGradient };
    const proxy = { t: 0 };

    this.tween = this.scene.tweens.add({
      targets: proxy,
      t: 1,
      duration: CROSSFADE_MS,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this.currentGradient = lerpGradient(from, this.targetGradient, proxy.t);
        this.rebuildTexture();
      },
      onComplete: () => {
        this.currentGradient = { ...this.targetGradient };
        this.rebuildTexture();
        this.tween = null;
      },
    });
  }

  private rebuildTexture(): void {
    if (this.destroyed || !isWebGL(this.scene)) return;

    const cam = this.scene.cameras.main;
    const w = cam.width;
    const h = cam.height;
    if (w <= 0 || h <= 0) return;

    const g = this.currentGradient;

    // Skip rendering when fully transparent.
    if (g.topAlpha <= 0.001 && g.bottomAlpha <= 0.001) {
      if (this.canvas) this.canvas.setVisible(false);
      return;
    }

    // Draw vertical gradient via a Graphics object → generateTexture.
    const gfx = this.scene.make.graphics({}, false);

    const steps = Math.min(h, 64); // 64 bands is visually smooth enough
    const bandH = Math.ceil(h / steps);

    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const color = lerpColor(g.topColor, g.bottomColor, t);
      const alpha = g.topAlpha + (g.bottomAlpha - g.topAlpha) * t;
      gfx.fillStyle(color, alpha);
      gfx.fillRect(0, i * bandH, w, bandH + 1);
    }

    // Destroy old texture if it exists, then generate fresh.
    if (this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
    gfx.generateTexture(this.textureKey, w, h);
    gfx.destroy();

    if (!this.canvas) {
      this.canvas = this.scene.add.image(0, 0, this.textureKey);
      this.canvas.setOrigin(0, 0);
      this.canvas.setScrollFactor(0);
      this.canvas.setDepth(SKY_DEPTH);
      this.canvas.setBlendMode(Phaser.BlendModes.ADD);
    } else {
      this.canvas.setTexture(this.textureKey);
    }

    this.canvas.setDisplaySize(w, h);
    this.canvas.setVisible(true);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    this.scene.scale.off(Phaser.Scale.Events.RESIZE, this.resizeListener);
    if (this.tween) {
      this.tween.stop();
      this.tween = null;
    }
    if (this.canvas) {
      this.canvas.destroy();
      this.canvas = null;
    }
    if (this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
  }
}
